import type { ReactNode } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton,
  Rating as MuiRating, Stack, Tooltip, Typography,
} from '@mui/material';
import { ContentCopy, OpenInNew, Place, TripOrigin } from '@mui/icons-material';
import { formatMoney } from '../../utils/currency';
import { formatRegisteredDateTime } from '../../utils/dateFormat';

export const REQUEST_KIND_LABELS: Record<string, string> = {
  ride: 'Ride',
  food: 'Food delivery',
  grocery: 'Grocery delivery',
  porter: 'Porter',
  service: 'Service job',
};

const TIMESTAMP_LABELS: Array<[string, string]> = [
  ['createdAt', 'Created'],
  ['scheduledAt', 'Scheduled for'],
  ['offeredAt', 'Offered to partner'],
  ['acceptedAt', 'Accepted'],
  ['arrivedAt', 'Arrived'],
  ['pickedUpAt', 'Picked up'],
  ['startedAt', 'Started'],
  ['deliveredAt', 'Delivered'],
  ['completedAt', 'Completed'],
  ['cancelledAt', 'Cancelled'],
  ['updatedAt', 'Last updated'],
];

type Earning = {
  gross?: number;
  commission?: number;
  commissionPercent?: number;
  payout?: number;
  status?: string;
  cashCollected?: boolean;
};

export function statusColor(status: string): 'success' | 'error' | 'warning' | 'info' | 'default' {
  const s = status.toLowerCase();
  if (['completed', 'delivered'].includes(s)) return 'success';
  if (['cancelled', 'canceled', 'rejected', 'expired', 'failed'].includes(s)) return 'error';
  if (['searching', 'pending', 'requested', 'offered', 'placed'].includes(s)) return 'warning';
  if (s) return 'info';
  return 'default';
}

function Field({ label, value, copy }: { label: string; value?: ReactNode; copy?: string }) {
  return (
    <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start" sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>{label}</Typography>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
          {value === undefined || value === null || value === '' ? '—' : value}
        </Typography>
        {copy && (
          <Tooltip title="Copy">
            <IconButton size="small" onClick={() => navigator.clipboard?.writeText(copy)}>
              <ContentCopy sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
}

const COMPLAINT_STATUS_COLOR: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  open: 'error',
  in_progress: 'warning',
  waiting_customer: 'info',
  resolved: 'success',
  closed: 'default',
};

export function complaintStatusColor(status: string) {
  return COMPLAINT_STATUS_COLOR[status] || 'default';
}

function ComplaintSection({ complaint }: { complaint: Record<string, unknown> }) {
  const status = String(complaint.status || '');
  return (
    <>
      <Typography variant="overline" color="text.secondary">Complaint</Typography>
      <Field label="Ticket" value={String(complaint.ticketNumber || '')} copy={String(complaint.ticketNumber || '') || undefined} />
      <Field
        label="Status"
        value={<Chip size="small" label={status.replace(/_/g, ' ')} color={complaintStatusColor(status)} sx={{ textTransform: 'capitalize' }} />}
      />
      <Field label="Reason" value={String(complaint.reason || '')} />
      <Field label="Details" value={String(complaint.description || '')} />
      <Field
        label="Complaint amount"
        value={complaint.amount === null || complaint.amount === undefined ? 'No linked trip' : formatMoney(Number(complaint.amount))}
      />
      <Field label="Partner" value={String(complaint.partnerName || '')} />
      <Field
        label="Raised by"
        value={`${String(complaint.raisedBy || '—')}${complaint.raisedByType ? ` (${String(complaint.raisedByType).replace(/_/g, ' ')})` : ''}`}
      />
      <Field label="Priority" value={String(complaint.priority || '')} />
      <Field label="Raised on" value={complaint.createdAt ? formatRegisteredDateTime(String(complaint.createdAt)) : ''} />
      {complaint.resolvedAt ? <Field label="Resolved on" value={formatRegisteredDateTime(String(complaint.resolvedAt))} /> : null}
      {complaint.resolution ? <Field label="Resolution" value={String(complaint.resolution)} /> : null}
    </>
  );
}

export const RATING_TYPE_LABELS: Record<string, string> = {
  ride: 'Ride',
  food_order: 'Food order',
  grocery_order: 'Grocery order',
  porter_order: 'Porter order',
  on_demand_booking: 'Service job',
  service_provider: 'Service provider',
  driver: 'Driver',
  restaurant: 'Restaurant',
  store: 'Store',
};

function RatingSection({ rating }: { rating: Record<string, unknown> }) {
  return (
    <>
      <Typography variant="overline" color="text.secondary">Rating</Typography>
      <Field label="Stars" value={<MuiRating value={Number(rating.rating || 0)} readOnly size="small" />} />
      <Field label="Review" value={String(rating.review || 'No written review')} />
      <Field
        label="Reviewer"
        value={[rating.reviewer, rating.reviewerPhone, rating.reviewerCode ? `ID #${rating.reviewerCode}` : '']
          .filter(Boolean).map(String).join(' · ')}
      />
      <Field label="Rated" value={RATING_TYPE_LABELS[String(rating.entityType)] || String(rating.entityType || '').replace(/_/g, ' ')} />
      <Field label="Rated on" value={rating.createdAt ? formatRegisteredDateTime(String(rating.createdAt)) : ''} />
      {rating.reply ? <Field label="Reply" value={String(rating.reply)} /> : null}
    </>
  );
}

export default function PartnerRequestDetailDialog({
  row,
  complaint,
  rating,
  onClose,
  onOpenFull,
  onOpenTicket,
}: {
  row: Record<string, unknown> | null;
  complaint?: Record<string, unknown> | null;
  rating?: Record<string, unknown> | null;
  onClose: () => void;
  onOpenFull?: (row: Record<string, unknown>) => void;
  onOpenTicket?: (complaint: Record<string, unknown>) => void;
}) {
  if (!row && !complaint && !rating) return null;
  const kind = String(row?.kind || '');
  const status = String(row?.status || '');
  const requestNumber = String(row?.requestNumber || '');
  const title = requestNumber || String(complaint?.ticketNumber || '') || (rating ? 'Rating' : 'Request');

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h6" fontWeight={800} sx={{ wordBreak: 'break-all' }}>{title}</Typography>
          {row && <Chip size="small" label={REQUEST_KIND_LABELS[kind] || kind || 'Request'} variant="outlined" />}
          {row && <Chip size="small" label={status.replace(/_/g, ' ') || 'unknown'} color={statusColor(status)} sx={{ textTransform: 'capitalize' }} />}
          {complaint && <Chip size="small" label="Complaint" color="warning" variant="outlined" />}
          {rating && <Chip size="small" label="Rating" color="success" variant="outlined" />}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {complaint && <ComplaintSection complaint={complaint} />}
        {rating && <RatingSection rating={rating} />}
        {(complaint || rating) && <Divider sx={{ my: 1.5 }} />}
        {row ? (
          <RequestSections row={row} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            This {rating ? 'rating' : 'complaint'} is not linked to a trip or booking, so there are no trip details to show.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {complaint && onOpenTicket && (
          <Button startIcon={<OpenInNew />} onClick={() => onOpenTicket(complaint)}>
            Open ticket
          </Button>
        )}
        {row && onOpenFull && (
          <Button startIcon={<OpenInNew />} onClick={() => onOpenFull(row)}>
            Open full record
          </Button>
        )}
        <Button variant="contained" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function RequestSections({ row }: { row: Record<string, unknown> }) {
  const kind = String(row.kind || '');
  const isService = kind === 'service';
  const status = String(row.status || '');
  const timestamps: Record<string, unknown> = { createdAt: row.createdAt, ...((row.timestamps || {}) as Record<string, unknown>) };
  const earning = row.earning as Earning | undefined;
  const requestNumber = String(row.requestNumber || '');

  return (
    <>
        <Typography variant="overline" color="text.secondary">Trip / request</Typography>
        <Field label="Request number" value={requestNumber} copy={requestNumber || undefined} />
        {isService && <Field label="Service" value={String(row.serviceName || '')} />}
        {isService && row.description ? <Field label="Job description" value={String(row.description)} /> : null}
        <Field label="Customer" value={[row.customer, row.customerPhone].filter(Boolean).join(' · ')} />

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="overline" color="text.secondary">Locations</Typography>
        {isService ? (
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.5 }}>
            <Place fontSize="small" color="error" sx={{ mt: 0.25 }} />
            <Box>
              <Typography variant="caption" color="text.secondary">Service location (customer address)</Typography>
              <Typography variant="body2" fontWeight={600}>{String(row.drop || '—')}</Typography>
              <Typography variant="caption" color="text.secondary">
                On-site job — the provider travels to this address, so there is no separate pickup point.
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={1} sx={{ py: 0.5 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TripOrigin fontSize="small" color="success" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Pickup</Typography>
                <Typography variant="body2" fontWeight={600}>{String(row.pickup || '—')}</Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Place fontSize="small" color="error" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Drop</Typography>
                <Typography variant="body2" fontWeight={600}>{String(row.drop || '—')}</Typography>
              </Box>
            </Stack>
          </Stack>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="overline" color="text.secondary">Amount</Typography>
        <Field label="Total amount" value={formatMoney(Number(row.totalAmount ?? 0))} />
        <Field label="Payment" value={[row.paymentMethod, row.paymentStatus].filter(Boolean).map(String).join(' · ').replace(/_/g, ' ')} />
        {earning ? (
          <>
            <Field
              label="Platform commission"
              value={`−${formatMoney(Number(earning.commission ?? 0))}${earning.commissionPercent ? ` (${earning.commissionPercent}%)` : ''}`}
            />
            <Field label="Partner earning" value={formatMoney(Number(earning.payout ?? 0))} />
            <Field
              label="Payout"
              value={earning.cashCollected ? 'Cash collected from customer' : String(earning.status || '').replace(/_/g, ' ')}
            />
          </>
        ) : (
          <Field label="Partner earning" value={status === 'completed' ? 'No settlement record' : 'Not settled (job not completed)'} />
        )}
        {row.cancelReason ? <Field label="Cancel reason" value={String(row.cancelReason)} /> : null}

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="overline" color="text.secondary">Timeline</Typography>
        {TIMESTAMP_LABELS.filter(([k]) => timestamps[k]).map(([k, label]) => (
          <Field key={k} label={label} value={formatRegisteredDateTime(String(timestamps[k]))} />
        ))}
    </>
  );
}
